import React, { useEffect, useRef, useMemo, useState } from 'react';
import { App, Flex, Typography, theme, Spin, Button } from 'antd';
import { UserOutlined, CopyOutlined, CheckOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CustomTooltip } from '~components/Tooltip';
import { NexusMessage } from './types';

import NexusLogo from './NexusLogo';
import './MessageList.css';

interface MessageListProps {
  messages: NexusMessage[];
  isLoading?: boolean;
}

/**
 * Displays conversation messages with auto-scroll
 */
export function MessageList({ messages, isLoading = false }: MessageListProps) {
  const { token } = theme.useToken();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastAssistantMessageIdRef = useRef<string | null>(null);

  // Find the last assistant message
  const lastAssistantMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return messages[i];
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0 || !containerRef.current) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const isNewAssistantMessage =
      lastMessage.role === 'assistant' && lastMessage.id !== lastAssistantMessageIdRef.current;

    if (isNewAssistantMessage && lastAssistantMessage) {
      // Update the ref immediately to prevent race conditions during streaming
      // This ensures subsequent re-renders won't schedule duplicate scrolls
      lastAssistantMessageIdRef.current = lastAssistantMessage.id;

      // Scroll to the top of the new assistant message
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        const messageElement = messageRefs.current.get(lastAssistantMessage.id);
        if (messageElement && containerRef.current) {
          // Calculate the position relative to the container's scrollable area
          const container = containerRef.current;
          const containerRect = container.getBoundingClientRect();
          const messageRect = messageElement.getBoundingClientRect();

          // Calculate scroll position: current scroll + (message top - container top)
          const scrollTop = container.scrollTop + (messageRect.top - containerRect.top);

          // Scroll to the top of the message with a small offset for better visibility
          container.scrollTop = Math.max(0, scrollTop - token.padding);
        }
      });
    } else if (lastMessage.role === 'user') {
      // For user messages, scroll to bottom
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, isLoading, lastAssistantMessage, token.padding]);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: token.padding,
      }}
    >
      <Flex vertical gap={token.marginLG}>
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} messageRefs={messageRefs} />
        ))}

        {isLoading && (
          <Flex
            align="center"
            gap={token.sizeXS}
            style={{
              padding: token.padding,
            }}
          >
            <Spin size="small" />
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Nexus is thinking...
            </Typography.Text>
          </Flex>
        )}

        <div ref={messagesEndRef} />
      </Flex>
    </div>
  );
}

type FencedCodeBlockProps = React.PropsWithChildren;

function FencedCodeBlock({ children }: FencedCodeBlockProps) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const codeBlockRef = useRef<HTMLPreElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeBlockRef.current?.textContent ?? '');
      setIsCopied(true);
      message.success('Copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      message.error('Failed to copy');
    }
  };

  return (
    <div className="nexus-code-block">
      <CustomTooltip
        placement="top"
        message="Copy code snippet"
        wrapperStyle={{
          position: 'absolute',
          top: token.paddingXXS,
          right: token.paddingXXS,
          zIndex: 1,
          lineHeight: 0,
        }}
      >
        <Button
          type="text"
          size="small"
          className="nexus-code-block-copy"
          aria-label="Copy code snippet"
          icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
          onClick={handleCopy}
          style={{
            color: isCopied ? token.colorSuccess : token.colorTextSecondary,
          }}
        />
      </CustomTooltip>
      <pre
        ref={codeBlockRef}
        style={{
          backgroundColor: token.colorBgLayout,
          border: `1px solid ${token.colorBorder}`,
          padding: token.padding,
          borderRadius: token.borderRadiusSM,
          overflow: 'auto',
          fontSize: token.fontSizeSM,
          fontFamily: 'monospace',
          margin: `${token.marginSM}px 0`,
        }}
      >
        {children}
      </pre>
    </div>
  );
}

function MessageBubble({
  message: msg,
  messageRefs,
}: {
  message: NexusMessage;
  messageRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
}) {
  const { token } = theme.useToken();
  const isAssistant = msg.role === 'assistant';
  const messageRef = useRef<HTMLDivElement>(null);

  // Register/unregister the message ref
  useEffect(() => {
    if (messageRef.current) {
      messageRefs.current.set(msg.id, messageRef.current);
    }
    return () => {
      messageRefs.current.delete(msg.id);
    };
  }, [msg.id, messageRefs]);

  return (
    <Flex
      ref={messageRef}
      gap={token.sizeXS}
      align="flex-start"
      style={{
        flexDirection: isAssistant ? 'row' : 'row-reverse',
      }}
    >
      {isAssistant ? (
        <NexusLogo size={32} />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: token.colorTextSecondary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          <UserOutlined />
        </div>
      )}

      <Flex
        vertical
        gap={token.sizeXXS}
        style={{
          maxWidth: '75%',
          alignItems: isAssistant ? 'flex-start' : 'flex-end',
        }}
      >
        <div
          style={{
            position: 'relative',
            padding: token.padding,
            borderRadius: token.borderRadius,
            backgroundColor: isAssistant ? token.colorBgContainer : token.colorPrimaryBg,
            boxShadow: token.boxShadowSecondary,
            wordBreak: 'break-word',
          }}
        >
          {msg.imageUrl && (
            <div style={{ marginBottom: token.marginXS }}>
              <img
                src={msg.imageUrl}
                alt="Screenshot"
                style={{
                  maxWidth: '100%',
                  borderRadius: token.borderRadiusSM,
                  border: `1px solid ${token.colorBorder}`,
                }}
              />
            </div>
          )}

          {isAssistant ? (
            <div
              className="nexus-message-content"
              style={{
                fontSize: token.fontSize,
                color: token.colorTextBase,
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // Headings
                  h1: ({ children }) => (
                    <h1
                      style={{
                        fontSize: token.fontSizeHeading3,
                        fontWeight: 600,
                        marginTop: token.marginMD,
                        marginBottom: token.marginSM,
                        lineHeight: token.lineHeightHeading3,
                      }}
                    >
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2
                      style={{
                        fontSize: token.fontSizeHeading4,
                        fontWeight: 600,
                        marginTop: token.marginSM,
                        marginBottom: token.marginXS,
                        lineHeight: token.lineHeightHeading4,
                      }}
                    >
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3
                      style={{
                        fontSize: token.fontSizeHeading5,
                        fontWeight: 600,
                        marginTop: token.marginSM,
                        marginBottom: token.marginXS,
                        lineHeight: token.lineHeightHeading5,
                      }}
                    >
                      {children}
                    </h3>
                  ),
                  // Paragraph
                  p: ({ children }) => <p style={{ margin: `${token.marginXS}px 0` }}>{children}</p>,
                  // Horizontal rule
                  hr: () => (
                    <hr
                      style={{
                        border: 'none',
                        borderTop: `1px solid ${token.colorBorder}`,
                        margin: `${token.marginMD}px 0`,
                      }}
                    />
                  ),
                  // Blockquote
                  blockquote: ({ children }) => (
                    <blockquote
                      style={{
                        borderLeft: `4px solid ${token.colorPrimary}`,
                        paddingLeft: token.paddingMD,
                        margin: `${token.marginSM}px 0`,
                        color: token.colorTextSecondary,
                        fontStyle: 'italic',
                      }}
                    >
                      {children}
                    </blockquote>
                  ),
                  // Code blocks
                  code: ({ inline, children, ...props }) =>
                    inline ? (
                      <code
                        style={{
                          backgroundColor: token.colorBgLayout,
                          color: token.colorText,
                          padding: '2px 6px',
                          borderRadius: token.borderRadiusSM,
                          fontSize: token.fontSizeSM,
                          fontFamily: 'monospace',
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <code
                        style={{
                          color: token.colorText,
                        }}
                        {...props}
                      >
                        {children}
                      </code>
                    ),
                  pre: ({ children }) => <FencedCodeBlock>{children}</FencedCodeBlock>,
                  // Lists
                  ul: ({ children }) => (
                    <ul
                      style={{
                        marginLeft: token.marginLG,
                        paddingLeft: token.marginMD,
                        listStyleType: 'disc',
                        listStylePosition: 'outside',
                        marginTop: token.marginXS,
                        marginBottom: token.marginXS,
                      }}
                    >
                      {children}
                    </ul>
                  ),
                  ol: ({ children, start }) => (
                    <ol
                      start={start}
                      style={{
                        marginLeft: token.marginLG,
                        paddingLeft: token.marginMD,
                        listStyleType: 'decimal',
                        listStylePosition: 'outside',
                        marginTop: token.marginXS,
                        marginBottom: token.marginXS,
                      }}
                    >
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li
                      style={{
                        marginBottom: token.marginXS,
                        paddingLeft: token.paddingXS,
                      }}
                    >
                      {children}
                    </li>
                  ),
                  // Text formatting
                  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                  // Links
                  a: ({ node, children, href }) => {
                    // LangGraph returns hosted images as clickable links: [![img](img-url)](host-url)
                    // We want to display the image but prevent navigation to the hosting service
                    const firstChild = node?.children?.[0];
                    const hasOnlyImage =
                      node?.children?.length === 1 &&
                      firstChild &&
                      firstChild.type === 'element' &&
                      firstChild.tagName === 'img';

                    if (hasOnlyImage) {
                      return <>{children}</>;
                    }

                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: token.colorLink,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {children}
                      </a>
                    );
                  },
                  // Tables
                  table: ({ children }) => (
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        margin: `${token.marginSM}px 0`,
                        border: `1px solid ${token.colorBorder}`,
                      }}
                    >
                      {children}
                    </table>
                  ),
                  thead: ({ children }) => (
                    <thead
                      style={{
                        backgroundColor: token.colorBgLayout,
                      }}
                    >
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => <tbody>{children}</tbody>,
                  tr: ({ children }) => (
                    <tr
                      style={{
                        borderBottom: `1px solid ${token.colorBorder}`,
                      }}
                    >
                      {children}
                    </tr>
                  ),
                  th: ({ children }) => (
                    <th
                      style={{
                        padding: token.paddingSM,
                        textAlign: 'left',
                        fontWeight: 600,
                        borderRight: `1px solid ${token.colorBorder}`,
                      }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      style={{
                        padding: token.paddingSM,
                        borderRight: `1px solid ${token.colorBorder}`,
                      }}
                    >
                      {children}
                    </td>
                  ),
                  // Images
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: token.borderRadiusSM,
                        border: `1px solid ${token.colorBorder}`,
                        marginTop: token.marginXS,
                        marginBottom: token.marginXS,
                      }}
                    />
                  ),
                }}
              >
                {msg.content}
              </ReactMarkdown>
            </div>
          ) : (
            <Typography.Text style={{ fontSize: token.fontSize }}>{msg.content}</Typography.Text>
          )}
        </div>
      </Flex>
    </Flex>
  );
}
